import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, MessageSquareOff, Pin } from "lucide-react";

import type { HTMLAttributes, ReactNode } from "react";
import type { Conversation_Types } from "@/types/(my-space)/ai-assistant.interface";
import { cn } from "@/utils/cn";

interface IProps extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  convTitle: string;
  /** Omit inside a dated group — the group heading already says "Today". */
  subtitle?: string;
  isPinned?: boolean;
  /** The open conversation — gets the rail and the tinted background. */
  isActive?: boolean;
  /** Disclosure control (expand/collapse), rendered before the title. */
  leadingSlot?: ReactNode;
  actions?: ReactNode;
  conversationType?: Conversation_Types;
  onClick?: () => void;
}

/**
 * One row in the chat history rail.
 *
 * Deliberately *not* a card with an icon tile: every conversation had the same
 * glyph and the same "• chat" tag, so the decoration carried no information and
 * cost a third of the row's width. What differs between rows is the title, how
 * recent it is, and whether it's the one you're in — so that is all the row
 * draws.
 *
 * The open-conversation area is a real `<button>` with the disclosure and row
 * actions as *siblings*, not children: nesting controls inside a clickable
 * container breaks keyboard use and makes every action also trigger the row.
 */
export const ConvoHistoryItem = ({
  convTitle,
  subtitle,
  isPinned,
  isActive,
  leadingSlot,
  actions,
  className,
  conversationType = "chat",
  onClick,
  ...divProps
}: IProps) => {
  const { t } = useTranslation("my-space");
  const hasTitle = !!convTitle?.trim();

  return (
    <div
      {...divProps}
      className={cn(
        "group/convo relative flex w-full shrink-0 items-center gap-1 overflow-hidden rounded-xs pe-1",
        "transition-colors duration-150",
        isActive ? "bg-primary-muted" : "hover:bg-muted",
        className,
      )}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="bg-primary absolute inset-y-1.5 start-0 w-0.5 rounded-full"
        />
      )}

      {leadingSlot}

      <button
        type="button"
        onClick={onClick}
        aria-current={isActive ? "true" : undefined}
        className={cn(
          "flex min-w-0 flex-1 flex-col items-start gap-0.5 text-start",
          // Single-line rows are half the height, so twice as much history
          // fits above the fold — the point of a history rail.
          subtitle ? "py-2" : "py-1.5",
          !leadingSlot && "ps-3",
          !onClick && "cursor-default",
        )}
      >
        <span
          className={cn(
            "flex w-full items-center gap-1.5 truncate text-xs font-semibold",
            isActive ? "text-primary-strong" : "text-foreground",
            !hasTitle && "text-muted-foreground font-normal italic",
          )}
        >
          {isPinned && (
            <Pin
              className="text-info size-3 shrink-0 rotate-45"
              aria-label={t("ai_assistant.pinned")}
            />
          )}
          <span className="truncate">
            {hasTitle ? convTitle : t("toasts.untitled")}
          </span>
        </span>

        {(subtitle || conversationType !== "chat") && (
          <span className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
            {subtitle}
            {/* Only worth saying when it *isn't* the default kind. */}
            {conversationType !== "chat" && (
              <>
                {subtitle && <span aria-hidden="true">·</span>}
                <span className="capitalize">{conversationType}</span>
              </>
            )}
          </span>
        )}
      </button>

      {actions && (
        <div
          className={cn(
            "shrink-0 opacity-0 transition-opacity duration-150",
            "focus-within:opacity-100 group-hover/convo:opacity-100",
            isActive && "opacity-100",
            "max-lg:opacity-100",
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * Recency bucket heading ("Today", "Previous 7 days", "Pinned").
 *
 * A rule runs from the label to the count badge, so groups read as bands rather
 * than as loose text above a list, and the whole band sticks while its rows
 * scroll under it. Pass `onToggle` to make the group collapsible — useful once
 * "Previous 30 days" holds forty chats you have to scroll past every time.
 */
export const ConvoGroupLabel = ({
  children,
  count,
  tone = "default",
  isCollapsed = false,
  onToggle,
}: {
  children: ReactNode;
  count?: number;
  tone?: "default" | "pinned";
  isCollapsed?: boolean;
  onToggle?: () => void;
}) => {
  const isPinned = tone === "pinned";

  const content = (
    <>
      {isPinned ? (
        <Pin
          className="text-info size-3 shrink-0 rotate-45"
          aria-hidden="true"
        />
      ) : (
        onToggle && (
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "text-muted-foreground size-3 shrink-0 transition-transform duration-150",
              isCollapsed && "-rotate-90",
            )}
          />
        )
      )}

      <span
        className={cn(
          "shrink-0 text-[10px] font-bold uppercase tracking-wider",
          isPinned ? "text-info" : "text-foreground",
        )}
      >
        {children}
      </span>

      <span
        aria-hidden="true"
        className={cn("h-px flex-1", isPinned ? "bg-info/25" : "bg-border")}
      />

      {count !== undefined && (
        <span
          className={cn(
            "grid size-4.5 shrink-0 place-items-center rounded-full text-[10px] font-bold tabular-nums",
            isPinned
              ? "bg-info-muted text-info"
              : "bg-muted text-muted-foreground",
          )}
        >
          {count}
        </span>
      )}
    </>
  );

  const shell =
    "surface-frosted sticky top-0 z-10 flex w-full items-center gap-2 px-3 pt-4 pb-1.5 first:pt-1";

  if (!onToggle) return <div className={shell}>{content}</div>;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!isCollapsed}
      className={cn(shell, "hover:bg-muted/60 text-start transition-colors")}
    >
      {content}
    </button>
  );
};

export const Skeletons = ({ count = 8 }: { count?: number }) =>
  [...Array(count)].map((_, i) => (
    <div key={i} className="flex flex-col gap-1.5 px-3 py-2">
      <Skeleton className="h-3" style={{ width: `${88 - (i % 4) * 14}%` }} />
      <Skeleton className="h-2 w-16" />
    </div>
  ));

export const EmptyDataState = ({
  label,
  hint,
}: {
  label?: string;
  hint?: string;
}) => {
  // Resolved at render, not as a default parameter: a module-level literal
  // would freeze in English (CLAUDE.md, i18n).
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-col items-center gap-1.5 px-3 py-8 text-center">
      <MessageSquareOff className="text-muted-foreground size-5" />
      <p className="text-foreground mb-0! text-sm font-medium">
        {label ?? t("ai_assistant.no_items_found")}
      </p>
      {hint && <p className="text-muted-foreground mb-0! text-xs">{hint}</p>}
    </div>
  );
};
